import { useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { Buffer } from 'buffer';

// Refresh token if it expires within this threshold (in milliseconds)
const TOKEN_EXPIRY_THRESHOLD_MS = 2 * 60 * 1000; // 2 minutes

/**
 * Hook that provides a function to ensure the access token is valid before critical API calls.
 * If the token is about to expire (within 2 minutes), it will be refreshed proactively.
 *
 * Usage:
 * const ensureValidToken = useEnsureValidToken();
 * await ensureValidToken(); // Call before critical API operations
 */
export const useEnsureValidToken = () => {
    const { instance, accounts } = useMsal();
    const request = { scopes: [`api://${import.meta.env.VITE_TOKEN_AUDIENCE}/api`] };

    const ensureValidToken = useCallback(async (): Promise<string> => {
        const expiryStr = sessionStorage.getItem('ACCESS_EXPIRY');
        const currentToken = sessionStorage.getItem('ACCESS_TOKEN');
        const now = Date.now();

        // Check if token exists and is still valid with buffer time
        if (expiryStr && currentToken) {
            const expiryTime = parseInt(expiryStr, 10);
            const timeUntilExpiry = expiryTime - now;

            // If token has more than threshold time remaining, it's still valid
            if (timeUntilExpiry > TOKEN_EXPIRY_THRESHOLD_MS) {
                return currentToken;
            }
        }

        // Token is expired or about to expire, refresh it
        try {
            const result = await instance.acquireTokenSilent({
                ...request,
                account: accounts[0],
                forceRefresh: true, // Force a fresh token
            });

            const { accessToken, idToken } = result;
            sessionStorage.setItem('ACCESS_TOKEN', accessToken);
            sessionStorage.setItem('ID_TOKEN', idToken);
            window.dispatchEvent(new Event('token'));

            // Update expiry time
            try {
                const payload = JSON.parse(
                    Buffer.from(accessToken.split('.')[1], 'base64').toString('ascii'),
                );
                sessionStorage.setItem('ACCESS_EXPIRY', (payload.exp * 1000).toString());
            } catch (err) {
                console.error('Failed to parse token expiry:', err);
            }

            return accessToken;
        } catch (err) {
            console.error('Failed to refresh token:', err);
            // Return current token as fallback - the API call may still work
            // or will fail with 401 which can be handled by the caller
            return currentToken || '';
        }
    }, [instance, accounts, request]);

    return ensureValidToken;
};

export default useEnsureValidToken;
